import { registerRecoveryDisowner } from "@utils/clearRecordingRecovery";

const callApi = async (
  ...args: Parameters<typeof import("@utils/callApi").callApi>
) => {
  const api = await import("@utils/callApi");
  return api.callApi(...args);
};

const LOCK = "medirect-recording";

export class RecordingOpenElsewhereError extends Error {
  constructor() {
    super("A recording is already open in another tab.");
    this.name = "RecordingOpenElsewhereError";
  }
}
let releaseLock: (() => void) | null = null;
let current: {
  caseNumber: string;
  submissionId: string;
  sessionId?: string;
  suspended?: boolean;
} | null = null;
let heartbeat: ReturnType<typeof setInterval> | null = null;
const sessions = new Map<string, string>();

export async function claimRecording(): Promise<void> {
  if (releaseLock) throw new Error("A recording is already open in this tab.");
  if (!navigator.locks) {
    // No cross-tab lock here; the server reservation still refuses a second recording.
    releaseLock = () => {};
    return;
  }
  await new Promise<void>((resolve, reject) => {
    void navigator.locks
      .request(LOCK, { ifAvailable: true }, async (lock) => {
        if (!lock) {
          reject(new RecordingOpenElsewhereError());
          return;
        }
        await new Promise<void>((release) => {
          releaseLock = release;
          resolve();
        });
      })
      .catch(reject);
  });
}

// Releases only if this submission still owns the tab, so a late send can't free a newer recording.
export function releaseRecordingFor(submissionId?: string): void {
  if (submissionId && current?.submissionId === submissionId) releaseRecording();
}

export function releaseRecording(): void {
  if (heartbeat) clearInterval(heartbeat);
  heartbeat = null;
  current = null;
  releaseLock?.();
  releaseLock = null;
}

function startHeartbeat(caseNumber: string, submissionId: string): void {
  if (heartbeat) clearInterval(heartbeat);
  let inFlight = false;
  heartbeat = setInterval(() => {
    if (inFlight) return;
    inFlight = true;
    void callApi("transcription/submissions/heartbeat", "POST", null, {
      case_number: caseNumber,
      submission_id: submissionId,
    })
      .catch((error: { status?: number }) => {
        // Network blips are fine: the 90 s lease outlives a couple of missed beats.
        if (error?.status === 409)
          void handleLostHeartbeat(caseNumber, submissionId);
      })
      .finally(() => {
        inFlight = false;
      });
  }, 20000);
}

// A 409 also follows a normal send (the reservation is no longer "recording"),
// so only warn when the case now belongs to a different submission.
async function handleLostHeartbeat(
  caseNumber: string,
  submissionId: string
): Promise<void> {
  if (current?.submissionId !== submissionId) return;
  if (heartbeat) clearInterval(heartbeat);
  heartbeat = null;
  try {
    const state = await callApi(
      `transcription/submissions/state/${caseNumber}`,
      "GET"
    );
    if (state.submission_id === submissionId) return;
  } catch {
    return;
  }
  const [{ store }, { showError }] = await Promise.all([
    import("@/store"),
    import("@/utils/notification"),
  ]);
  showError(
    "Another tab or device has started a recording for this case. This recording can't be sent: stop it and download the audio.",
    store.dispatch
  );
}

export async function prepareRecording(
  caseNumber: string,
  expectedId: string | null,
  overwrite: boolean,
  submissionId: string = crypto.randomUUID(),
  replaceId: string | null = null
): Promise<string> {
  const response = await callApi("transcription/submissions/prepare", "POST", null, {
    case_number: caseNumber,
    submission_id: submissionId,
    expected_submission_id: expectedId,
    overwrite,
    replace_submission_id: replaceId,
  });
  current = { caseNumber, submissionId: response.submission_id };
  startHeartbeat(caseNumber, response.submission_id);
  return response.submission_id;
}

export function bindRecordingSession(
  sessionId: string,
  submissionId?: string
): string | undefined {
  const id = submissionId ?? current?.submissionId;
  if (id) sessions.set(sessionId, id);
  if (current) {
    current.sessionId = sessionId;
    current.suspended = false;
  }
  return id;
}
export const submissionForSession = (sessionId: string | null) =>
  sessionId ? sessions.get(sessionId) : current?.submissionId;
export const currentSubmission = () => current?.submissionId;
export async function discardRecording(): Promise<void> {
  const saved = current;
  try {
    if (saved)
      await callApi("transcription/submissions/discard", "POST", null, {
        case_number: saved.caseNumber,
        submission_id: saved.submissionId,
      });
  } finally {
    releaseRecording();
  }
}

/** This tab holds the recording lock, and for `caseNumber` when one is given. */
export const ownsRecordingHere = (caseNumber?: string) =>
  releaseLock !== null && (!caseNumber || current?.caseNumber === caseNumber);
export async function isRecordingClaimed(): Promise<boolean> {
  const state = await navigator.locks?.query();
  return !!state?.held?.some((lock) => lock.name === LOCK);
}
export async function recoverOwnership(
  caseNumber: string,
  submissionId: string
): Promise<void> {
  if (current?.submissionId === submissionId && releaseLock) return;
  await claimRecording();
  try {
    const state = await callApi(
      `transcription/submissions/state/${caseNumber}`,
      "GET"
    );
    if (state.submission_id !== submissionId)
      throw new Error(
        "This recording has been replaced. Download the saved audio before discarding it."
      );
    current = { caseNumber, submissionId };
    startHeartbeat(caseNumber, submissionId);
  } catch (error) {
    releaseRecording();
    throw error;
  }
}

registerRecoveryDisowner(async () => {
  releaseRecording();
  sessions.clear();
});
export async function discardSavedOwnership(
  caseNumber: string | null,
  submissionId?: string
): Promise<void> {
  if (!current || current.submissionId !== submissionId) await claimRecording();
  try {
    if (caseNumber && submissionId) {
      try {
        await callApi("transcription/submissions/discard", "POST", null, {
          case_number: caseNumber,
          submission_id: submissionId,
        });
      } catch (error) {
        if ((error as { status?: number }).status !== 409) throw error;
      }
    }
  } finally {
    releaseRecording();
  }
}

export async function saveRecordingNotes(
  caseNumber: string,
  notes: string
): Promise<void> {
  await callApi("transcription/submissions/notes", "PUT", null, {
    case_number: caseNumber,
    notes,
  });
  const { queryClient } = await import("@utils/reactQueryClient");
  const { CLIENT_KEYS } = await import("@/constants/clientKeys");
  void queryClient.invalidateQueries({
    queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
  });
}

export function markRecordingSuspended(): void {
  if (current) current.suspended = true;
}
export async function isSavedRecordingUnavailable(): Promise<boolean> {
  return (await isRecordingClaimed()) && !current?.suspended;
}
export async function authorizeRecordingResume(
  caseNumber: string,
  submissionId: string
): Promise<void> {
  await recoverOwnership(caseNumber, submissionId);
  markRecordingSuspended();
  try {
    await callApi("transcription/submissions/heartbeat", "POST", null, {
      case_number: caseNumber,
      submission_id: submissionId,
    });
  } catch (error) {
    if (
      (error as { status?: number }).status === 409 &&
      current?.submissionId === submissionId
    ) {
      if (heartbeat) clearInterval(heartbeat);
      heartbeat = null;
    }
    throw error;
  }
}
